import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Form, Formik, useFormik } from 'formik';
import * as Yup from 'yup';
import { camelize, decamelizeKeys } from 'humps';
import { travelDurationHoursToSeconds } from '../../utils/travelDuration';
import { parse, isValid, format, differenceInCalendarDays } from 'date-fns';
import cx from 'classnames'
import uniqBy from "lodash/uniqBy";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';

import { RouteMapContainer } from '../../containers/Route/RouteMapContainer';
import { Card } from '../Card/Card';
import { TimeLine } from '../TimeLine/TimeLine';
import { LoadingContext } from '../Loading/Loading';
import { RouteForm } from './RouteForm';
import { Button } from '../Button/Button';
import { useFilter } from '../../hooks/useFilter';
import { ConfirmSaveModal } from '../RouteOrder/ConfirmSaveModal';
import { routesApi } from '../../api/RoutesApi';
import { VolumeInfo } from './VolumeInfo';
import { OrderType, RouteElementType } from '../../utils/utils';
import { getDaysInStock } from '../../utils/dateUtils';
import { MapFilters } from './MapFilters';
import { matchesProductTypeFilter } from '../../utils/productTypeFilter';
import { initialEndPoint } from '../../utils/routeEndPoint';

const LOCALE = 'pl-PL';
const TIMEZONE = 'Europe/Warsaw';

export const hasChangedFields = (values, initialValues, excludedFields = []) => {
  return Object.keys(values).some(
    key =>
      !excludedFields.includes(key) &&
      values[key] !== initialValues[key]
  );
};

export const RouteEdit = ({
  orders,
  drivers,
  routeOrders,
  route,
  ramps,
  cars,
  phones,
  statusWarehouseId,
  orderMissingServicesPrices,
  onUpdateOrder,
  onUpdateRouteOrders,
  onUpdateRouteOrder,
  hasReturnPicking = false,
}) => {
  const submitLockRef = useRef(false);
  const allowNavigationRef = useRef(false);
  const timelineRunIdRef = useRef(0);
  const travelToFirstPointMs = useRef(null);
  const senders = useMemo(() => uniqBy(orders.map(order => order.sender), (sender) => sender.id), [orders]);
  const services = useMemo(
    () => ([
      {
        id: -1,
        name: "Bez usługi"
      },
      ...uniqBy(orders.map(order => order?.orderServices?.map((serv) => serv?.service))?.flat()?.filter(serv => serv !== undefined), (service) => service?.id),
    ]),
    [orders]
  );

  const statuses = useMemo(() => uniqBy(orders.map(order => order.status)?.flat(), (status) => status.id), [orders]);
  const productTypes = useMemo(
    () => [
      { id: -1 },
      ...uniqBy(
        orders.flatMap(order => order.packages?.flatMap(pkg => pkg.product?.productTypes ?? []) ?? []),
        (pt) => pt.id
      ),
    ],
    [orders]
  );
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [confirmationsFilter, setConfirmationsFilter] = useState(null);
  const [warehouseDaysFilter, setWarehouseDaysFilter] = useState(null);
  const [validatedRouteOrders, setValidatedRouteOrders] = useState([])
  const [isOpenConfirmSaveModal, setIsOpenConfirmSaveModal] = useState(false)
  const [isOpenDeliveryWarningModal, setIsOpenDeliveryWarningModal] = useState(false);
  const [isOpenFirstPointGapWarningModal, setIsOpenFirstPointGapWarningModal] = useState(false);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [hasRouteOrdersChanged, setHasRouteOrdersChanged] = useState(false);
  const [driverBreaks, setDriverBreaks] = useState([]);
  const [showSubmitValidationError, setShowSubmitValidationError] = useState(false);
  const [routeResult, setRouteResult] = useState(null);
  const [isRouteDirty, setIsRouteDirty] = useState(true);

  const {
    selectedValues: selectedServices,
    filterClickHandler: filterServiceHandler,
  } = useFilter("services");
  const {
    selectedValues: selectedSenders,
    filterClickHandler: filterSendersHandler,
  } = useFilter("senders");
  const {
    selectedValues: selectedOrderTypes,
    filterClickHandler: orderTypesHandler,
  } = useFilter("orderTypes")
  const {
    selectedValues: selectedStatuses,
    filterClickHandler: filterStatusesHandler,
  } = useFilter("statuses");
  const {
    selectedValues: selectedLocationFilter,
    filterClickHandler: filterLocationFilterHandler,
  } = useFilter("location-filter");
  const {
    selectedValues: selectedIsUrgentFilter,
    filterClickHandler: filterIsUrgentHandler,
  } = useFilter("is-urgent");
  const {
    selectedValues: selectedIsExchangeFilter,
    filterClickHandler: filterIsExchangeHandler,
  } = useFilter("is-exchange");
  const {
    selectedValues: selectedIsReturnFilter,
    filterClickHandler: filterIsReturnHandler,
  } = useFilter("is-return");
  const {
    selectedValues: selectedIsOrderFilter,
    filterClickHandler: filterIsOrderHandler,
  } = useFilter("is-order");
  const {
    selectedValues: selectedProductTypes,
    filterClickHandler: filterProductTypesHandler,
  } = useFilter("productTypes");
  const getConfirmationCount = (order) => order.deliveryConfirmationCount ?? 0;
  const isInCompactRange = (value, range, plusThreshold) => {
    if (!range) {
      return true;
    }

    const start = Math.min(range.start, range.end);
    const end = Math.max(range.start, range.end);

    if (end >= plusThreshold) {
      return value >= start;
    }

    return value >= start && value <= end;
  };

  const filteredOrders = useMemo(() =>
    orders.filter((order) =>
      selectedSenders.length === 0
        ? selectedSenders
        : selectedSenders?.some((senderId) => senderId === order.sender.id.toString())
    )
      ?.filter((order) =>
        selectedServices.length === 0
          ? selectedServices
          : selectedServices?.includes('-1') ? order?.orderServices?.length === 0 : selectedServices?.some((serviceId) => order?.orderServices?.map(serv => serv?.service?.id?.toString()).includes(serviceId))
      )
      ?.filter((order) =>
        selectedStatuses.length === 0
          ? selectedStatuses
          : selectedStatuses?.some((statusId) => statusId === order.status.id.toString())
      )
      ?.filter((order) =>
        selectedOrderTypes.length !== 1 || order.type === selectedOrderTypes[0]
      )
      ?.filter((order) => {
        if (selectedLocationFilter[0] === 'warehouse') {
          return parseInt(statusWarehouseId) === parseInt(order.status.id);
        } else if (selectedLocationFilter[0] === 'visible_by_pickup_at') {
          return order.isVisibleByPickupAt;
        }

        return true;
      })
      ?.filter((order) => {
        if (selectedIsUrgentFilter[0] === "1") {
          return order.isUrgent;
        }

        return true;
      })
      ?.filter((order) => {
        if (selectedIsExchangeFilter[0] === "1") {
          return order.isExchange;
        }

        return true;
      })
      ?.filter((order) => {
        if (selectedIsReturnFilter[0] === "1") {
          return order.type === 'return';
        }

        return true;
      })
      ?.filter((order) => {
        if (selectedIsOrderFilter[0] === "1") {
          return order.type === 'order' && !order.isExchange;
        }

        return true;
      })
      ?.filter((order) => {
        if (confirmationsFilter === null) return true;

        const count = getConfirmationCount(order);

        return isInCompactRange(count, confirmationsFilter, 5);
      })
      ?.filter((order) => {
        if (warehouseDaysFilter === null) return true;

        const days = getDaysInStock(order);

        if (days === null) return false;

        return isInCompactRange(days, warehouseDaysFilter, 10);
      })
      ?.filter((order) => matchesProductTypeFilter(order, selectedProductTypes)),
    [
      orders,
      selectedSenders,
      selectedServices,
      selectedStatuses,
      selectedOrderTypes,
      selectedLocationFilter,
      selectedIsUrgentFilter,
      selectedIsExchangeFilter,
      selectedIsReturnFilter,
      selectedIsOrderFilter,
      confirmationsFilter,
      warehouseDaysFilter,
      selectedProductTypes,
    ]);
  const setIsLoading = useContext(LoadingContext);

  const {
    confirmedVolumeM3,
    totalVolumeM3,
    confirmedIncomePrice,
    totalIncomePrice,
    isNotPricedPackages,
    orderConfirmedIncomePrice,
    orderTotalIncomePrice,
    exchangeConfirmedIncomePrice,
    exchangeTotalIncomePrice
  } = useMemo(() => {
    return (routeOrders ?? []).reduce(
      (acc, { order }) => {
        const v = Number(order?.packagesVolumeM3) || 0;

        if (order?.type === 'order') {
          acc.totalVolumeM3 += v;
        }

        acc.totalIncomePrice += Number(order?.incomePrice?.price?.amount ?? 0);
        acc.orderTotalIncomePrice += Number(order?.incomePrice?.delivery?.amount ?? 0);
        acc.exchangeTotalIncomePrice += Number(order?.incomePrice?.return?.amount ?? 0);

        if (order?.incomePrice?.notPricedPackages?.length > 0) {
          acc.isNotPricedPackages = true;
        }

        if (order?.deliveryConfirmations?.filter((confirmation) => confirmation.routeId === route.id)?.[0]?.status === "confirmed") {
          if (order?.type === 'order') {
            acc.confirmedVolumeM3 += v;
          }

          acc.confirmedIncomePrice += Number(order?.incomePrice?.price?.amount ?? 0);
          acc.orderConfirmedIncomePrice += Number(order?.incomePrice?.delivery?.amount ?? 0);
          acc.exchangeConfirmedIncomePrice += Number(order?.incomePrice?.return?.amount ?? 0);
        }

        return acc;
      },
      {
        confirmedVolumeM3: 0,
        totalVolumeM3: 0,
        confirmedIncomePrice: 0,
        totalIncomePrice: 0,
        isNotPricedPackages: false,
        orderConfirmedIncomePrice: 0,
        orderTotalIncomePrice: 0,
        exchangeConfirmedIncomePrice: 0,
        exchangeTotalIncomePrice: 0
      }
    );
  }, [JSON.stringify(routeOrders)]);

  const normalizeTime = (value) => {
    if (!value) return '';

    const timeFormats = [
      'H:m:s',
      'HH:mm:ss',
      'H:m',
      'HH:mm',
    ];

    const parsed = timeFormats
      .map((format) => parse(value, format, new Date()))
      .find((time) => isValid(time))

    return parsed ? format(parsed, 'HH:mm') : '';
  };

  const toValidDate = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
      return isValid(value) ? value : null;
    }

    if (typeof value === 'string') {
      const parsed = parse(value, 'yyyy-MM-dd HH:mm', new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    }

    const nativeDate = new Date(value);
    return isValid(nativeDate) ? nativeDate : null;
  };

  const formik = useFormik({
    initialValues: {
      id: route.id,
      name: route.name || '',
      speed: route.speed ?? 0,
      driverId: route.driverId || null,
      loadingAt: route.loadingAt || '',
      unloadingTime: 30,
      // Legacy fields (kept for timeline calculations)
      startTime: normalizeTime(route.startTime),
      routeDate: route.routeDate || '',
      // Combined departure datetime
      departureAt: (route.routeDate && route.startTime) ? `${route.routeDate} ${normalizeTime(route.startTime)}` : '',
      startPoint: route.startPoint || '',
      endPoint: initialEndPoint(route),
      endPointDistance: route.endPointDistance || null,
      endPointArrivalDatetime: route.endPointArrivalDatetime ? new Date(route.endPointArrivalDatetime) : null,
      endPointVia: route.endPointVia ? JSON.parse(route.endPointVia) : [],
      carId: route.carId || null,
      rampNumber: route.rampNumber || '',
      routeOrders: [],
      firstPickupAt: route.firstPickupAt || null,
      planningMode: route?.planningMode ?? 'departure',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Required'),
      startPoint: Yup.string().required('Required'),
      speed: Yup.number().required('Required'),
      driverId: Yup.string().nullable(),
      loadingAt: Yup.string().nullable(),
      departureAt: Yup.string().required(__('routes.validation.departure_required')),
    }),
    onSubmit: async (values, formik) => {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        submitLockRef.current = false;
        return;
      }

      setIsLoading(true);

      const result = { ...values };

      // Send combined departure datetime
      if (result.departureAt) {
        const departureAtDate = toValidDate(result.departureAt);
        result.departureAt = departureAtDate ? format(departureAtDate, 'yyyy-MM-dd HH:mm:ss') : null;
      }

      // Send first pickup datetime
      if (result.firstPickupAt && result.firstPickupAt !== '') {
        const firstPickupAtDate = toValidDate(result.firstPickupAt);
        result.firstPickupAt = firstPickupAtDate ? format(firstPickupAtDate, 'yyyy-MM-dd HH:mm:ss') : null;
      }

      if (result.loadingAt !== '') {
        const loadingAtDate = toValidDate(result.loadingAt);
        result.loadingAt = loadingAtDate ? format(loadingAtDate, 'yyyy-MM-dd HH:mm:ss') : null;
      }

      result.routeOrders = result.routeOrders.map((ro) => {
        const arrivalDatetime = toValidDate(ro.arrivalDatetime);

        return {
          ...ro,
          arrival_time: ro.arrivalTime?.toLocaleString(LOCALE, { timezone: TIMEZONE }),
          arrival_datetime: arrivalDatetime ? format(arrivalDatetime, 'yyyy-MM-dd HH:mm:ss') : null,
        };
      });

      result.endPointArrivalDatetime = values.endPointArrivalDatetime
        ? format(values.endPointArrivalDatetime, 'yyyy-MM-dd HH:mm:ss')
        : values.endPointArrivalDatetime;

      try {
        const response = await fetch(`/admin/routes/save_route`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(decamelizeKeys(result)),
        });

        if (response.status === 422) {
          const { errors = {} } = await response.json();

          // Map every validation error onto the corresponding formik field so
          // the user sees which field is broken (converting snake_case keys
          // returned by Laravel to the camelCase used by formik values).
          Object.entries(errors).forEach(([key, messages]) => {
            if (!Array.isArray(messages) || messages.length === 0) return;
            const fieldName = camelize(key);
            formik.setFieldError(fieldName, messages[0]);
            formik.setFieldTouched(fieldName, true, false);
          });

          // Show the actual validation messages in the toast instead of a
          // generic 'Nie udało się zapisać trasy!' so the user knows what to fix.
          const flatMessages = Object.values(errors)
            .flat()
            .filter(Boolean);
          const toastBody = flatMessages.length > 0
            ? flatMessages.join('\n')
            : __('routes.save_error');
          toastr.error(toastBody, __('routes.save_error'));

          setShowSubmitValidationError(true);
          submitLockRef.current = false;
          setIsLoading(false);
          return;
        }

        if (!response.ok) {
          // Try to surface the server-side message (e.g. from an exception
          // handler) so the user has a clue what went wrong rather than just a
          // generic failure toast.
          let serverMessage = '';
          try {
            const body = await response.json();
            serverMessage = body.message || body.error || '';
          } catch {
            // response had no JSON body — fall back to the generic message
          }
          submitLockRef.current = false;
          setIsLoading(false);
          toastr.error(serverMessage || `${__('routes.save_error')} (${response.status})`);
          return;
        }

        const data = await response.json();

        allowNavigationRef.current = true;
        setAllowNavigation(true);

        window.location.href = `/admin/routes/show/${data.id}`;
      } catch (error) {
        submitLockRef.current = false;
        setIsLoading(false);
        toastr.error(__('routes.save_error'));
      }
    },
  });

  // -- NEW: handlers to block/confirm navigation when form contains changes --
  useEffect(() => {
    const excludedFields = ['endPointArrivalDatetime', 'endPointVia', 'routeOrders'];

    const beforeUnloadHandler = (e) => {
      if (allowNavigationRef.current) return undefined;

      const isDirty = hasChangedFields(
        formik.values,
        formik.initialValues,
        excludedFields,
      );

      if ((isDirty || hasRouteOrdersChanged) && !allowNavigation) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }

      return undefined;
    };

    const clickHandler = (e) => {
      const isDirty = hasChangedFields(
        formik.values,
        formik.initialValues,
        excludedFields,
      );

      const anchor = e.target.closest && e.target.closest('a');

      if (!anchor) return;
      if ((isDirty || hasRouteOrdersChanged) && !allowNavigation) {}
    };

    const popStateHandler = (e) => {
      const isDirty = hasChangedFields(
        formik.values,
        formik.initialValues,
        excludedFields,
      );

      if ((isDirty || hasRouteOrdersChanged) && !allowNavigation) {}
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    document.addEventListener('click', clickHandler, true);
    window.addEventListener('popstate', popStateHandler);

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      document.removeEventListener('click', clickHandler, true);
      window.removeEventListener('popstate', popStateHandler);
    };
  }, [allowNavigation, formik.values, hasRouteOrdersChanged]);

  // rest of your code remains the same
  useEffect(() => {
    if (formik.values.routeOrders.length > 0 && formik.values.firstPickupAt === null) {
      const first = formik.values.routeOrders[0];
      if (first?.arrivalDatetime) {
        const dt = new Date(first.arrivalDatetime);
        dt.setSeconds(0, 0);
        formik.setFieldValue('firstPickupAt', format(dt, 'yyyy-MM-dd HH:mm'));
      }
    }
  }, [formik.values.routeOrders]);

  const [hideForm, setHideForm] = useState(true);
  const [distance, setDistance] = useState(0);
  const [directions, setDirections] = useState([]);
  const [isTimelineCalculating, setIsTimelineCalculating] = useState(false);

  const getFallbackTravelToFirstPointMs = () => {
    const firstLeg = directions?.[0]?.directions?.routes?.[0]?.legs?.[0];
    const firstLegDurationInSec = firstLeg?.duration_in_traffic?.value ?? firstLeg?.duration?.value;
    const speedModifier = (parseFloat(formik.values.speed) || 0) / 60;

    if (!Number.isFinite(firstLegDurationInSec) || speedModifier <= 0) {
      return null;
    }

    const adjustedTravelMs = (firstLegDurationInSec * 1000) / speedModifier;

    return Number.isFinite(adjustedTravelMs)
      ? Math.round(adjustedTravelMs / 60000) * 60000
      : null;
  };

  const effectiveTravelToFirstPointMs = travelToFirstPointMs.current ?? getFallbackTravelToFirstPointMs();

  const departureTime = useMemo(() => {
    if (formik.values.departureAt) {
      return parse(formik.values.departureAt, 'yyyy-MM-dd HH:mm', new Date());
    }
    if (formik.values.routeDate && formik.values.startTime) {
      return parse(`${formik.values.routeDate} ${formik.values.startTime}`, 'yyyy-MM-dd HH:mm', new Date());
    }
    return new Date();
  }, [formik.values.departureAt, formik.values.routeDate, formik.values.startTime])

  useEffect(() => {
    if (!directions || hideForm) {
      return;
    }

    renderTimeLine();
  }, [directions, routeOrders, formik.values.startTime, formik.values.routeDate]);

  const renderTimeLine = async () => {
    const runId = ++timelineRunIdRef.current;
    setIsTimelineCalculating(true);

    const hasValidDeparture = !!(formik.values.routeDate && formik.values.startTime);

    let waypointDateTime = null;
    let startTimeParts = null;

    if (hasValidDeparture) {
      waypointDateTime = new Date(formik.values.routeDate);
      if (Number.isNaN(waypointDateTime.getTime())) {
        if (runId === timelineRunIdRef.current) {
          setIsTimelineCalculating(false);
        }
        return;
      }
      startTimeParts = formik.values.startTime.split(':');
      if (startTimeParts.length < 2 || Number.isNaN(parseInt(startTimeParts[0]))) {
        if (runId === timelineRunIdRef.current) {
          setIsTimelineCalculating(false);
        }
        return;
      }
    }

    // Must mirror useMapApi.renderStagesOnMap. In edit mode RouteMap.js
    // passes hideInactiveRouteOrders=false, so hidden orders
    // (SENT/RECEIVED/CANCELLED) ARE included in the waypoint list sent to
    // the routing engine and produce real legs. We therefore include them
    // here as well — keeping tmpRouteOrders aligned with directions.legs
    // (one shifted routeOrder per non-endpoint leg). Filtering them out
    // would mis-align shift() and either clobber endPointDistance or drop
    // these orders from formik state on the next setFieldValue.
    // (Ticket 869d68nm0 — gotowe trasy znikały po wejściu w edycję.)
    const visibleRouteOrders = routeOrders.filter(
      (element) => element.type === RouteElementType.ROUTE_ORDER
    );

    // Coherence guard against stale directions.
    // Scenario: user runs Optimize (directions have N legs for N visible orders),
    // then attaches k more orders via marker click. The [routeOrders] dep above
    // fires this effect BEFORE the next Optimize computes new directions. At
    // that moment visibleRouteOrders.length = N+k but totalLegs = N (legs)
    // or N+1 (legs + endpoint). Running the loop here would build a
    // newRouteOrders of only N entries and then formik.setFieldValue(
    // 'routeOrders', …) would silently DROP the k recently-attached orders
    // from formik state. When the 2nd Optimize then arrives, the TimeLine
    // renders over a formik whose routeOrders are partially hydrated
    // (missing fields on the dropped entries propagated via downstream
    // merges) — producing the 'white screen' reported on 2026-04-21.
    // Safer: skip this run entirely when directions clearly predate the
    // current routeOrders; the next setDirections() will trigger a fresh
    // coherent render.
    const totalLegs = directions
      .filter((d) => d?.directions?.routes?.length > 0)
      .reduce((sum, d) => sum + (d.directions.routes[0].legs?.length ?? 0), 0);
    if (totalLegs > 0 && visibleRouteOrders.length > totalLegs) {
      if (runId === timelineRunIdRef.current) {
        setIsTimelineCalculating(false);
      }
      return;
    }

    let tmpRouteOrders = [...visibleRouteOrders];
    let tmpRouteDriverBreaks = routeOrders.filter((element) => element.type === RouteElementType.DRIVER_BREAK_TIME);
    let tmpUnloadingTimes = [...visibleRouteOrders];

    const newRouteOrders = [];
    const speed = formik.values.speed / 60;
    let distanceSum = 0;
    let departureMs = null;
    let hasEndPointLeg = false;

    if (hasValidDeparture) {
      waypointDateTime.setHours(parseInt(startTimeParts[0]), parseInt(startTimeParts[1]));
      departureMs = waypointDateTime.getTime();
    }

    directions.filter(d => d?.directions?.routes?.length > 0).map(({ directions: { routes } }) => {
      const { legs } = routes[0];
      legs.map((leg, index) => {
        const { distance, duration, duration_in_traffic: durationInTraffic } = leg;
        // Normalize: Google wraps in {value: N}, OSRM gives plain numbers
        const distanceM = distance?.value ?? distance ?? 0;
        const durationS = duration?.value ?? duration ?? 0;
        const durationInTrafficS = durationInTraffic?.value ?? durationInTraffic ?? null;

        let previousOrderUnloadingTime = '0';
        let driverBreakTime = 0;

        const routeOrder = tmpRouteOrders.shift();

        // routeOrder is undefined on the final "endpoint leg" (origin → end).
        // Earlier legs always have a routeOrder matching the shifted waypoint.
        if (routeOrder && (index !== 0 || parseInt(routeOrder.stage) !== 0)) {
          const previousOrderUnloadingTimeElement = tmpUnloadingTimes.shift();
          previousOrderUnloadingTime = previousOrderUnloadingTimeElement?.unloadingTime ?? formik.values.unloadingTime;
        }

        if (routeOrder && tmpRouteDriverBreaks?.some((element) => element.ordering < routeOrder.ordering && element.stage === routeOrder.stage)) {
          const driverBreakTimeElement = tmpRouteDriverBreaks.shift();
          driverBreakTime = driverBreakTimeElement.durationInMin;
        }

        const delayInMs = (parseInt(previousOrderUnloadingTime) + driverBreakTime) * 60 * 1000;
        const legDistance = Math.round(distanceM / 1000);

        const effectiveDurationS = durationInTrafficS ?? durationS;
        const googleMapsAvgSpeed = effectiveDurationS === 0 ? 0 : (distanceM / effectiveDurationS) * 3.6;
        const customSpeed = googleMapsAvgSpeed * speed;
        const customDuration = customSpeed === 0 ? 0 : distanceM / 1000 / customSpeed;

        distanceSum += legDistance;

        let arrivalDatetime = null;
        let arrivalTime = null;

        if (hasValidDeparture && waypointDateTime) {
          const travelMs = customDuration * 60 * 60 * 1000;
          if (!Number.isFinite(travelMs)) {
            // Skip time calculation if travel time is not finite
          } else {
            waypointDateTime.setTime(waypointDateTime.getTime() + travelMs);
            arrivalDatetime = new Date(waypointDateTime.getTime() + delayInMs);
            waypointDateTime.setTime(waypointDateTime.getTime() + delayInMs);
            arrivalTime = new Date(waypointDateTime.getTime());
          }
        }

        if (!routeOrder) {
          // endPoints
          hasEndPointLeg = true;
          formik.setFieldValue('endPointDistance', legDistance);
          formik.setFieldValue('endPointArrivalDatetime', arrivalTime);
        } else {
          const ro = formik.values.routeOrders.find(fro => fro.orderId === routeOrder.orderId)
          // regular point
          newRouteOrders.push({
            ...routeOrder,
            unloadingTime: routeOrder?.unloadingTime === undefined ? formik.values.unloadingTime : routeOrder?.unloadingTime,
            dailyBreakMinutes: routeOrder?.dailyBreakMinutes ?? 0,
            distance: legDistance,
            travelDurationSeconds: travelDurationHoursToSeconds(customDuration),
            arrivalTime,
            arrivalDatetime,
            type: RouteElementType.ROUTE_ORDER,
            // custom info
            speed: Math.round(customSpeed),
            products: ro && ro.products || null,
          });
        }
      });
    });

    if (hasValidDeparture && newRouteOrders.length > 0 && newRouteOrders[0].arrivalDatetime && departureMs) {
      // Round to full minutes to avoid off-by-one-minute display issues
      travelToFirstPointMs.current = Math.round((newRouteOrders[0].arrivalDatetime.getTime() - departureMs) / 60000) * 60000;
    } else {
      // Reset when route has no valid departure or no orders — prevents stale value
      travelToFirstPointMs.current = null;
    }

    // No return leg was calculated (no end point, or it could not be
    // geocoded): a distance loaded from the saved route is stale and must not
    // be saved, otherwise the printed route list adds a leg the map never drew.
    if (!hasEndPointLeg) {
      formik.setFieldValue('endPointDistance', null);
      formik.setFieldValue('endPointArrivalDatetime', null);
    }

    const calculatedRouteOrders = await calculateArrivedTime(newRouteOrders);

    if (runId !== timelineRunIdRef.current) {
      return;
    }

    const finalRouteOrders = calculatedRouteOrders
      .concat(routeOrders.filter((element) => element.type === RouteElementType.DRIVER_BREAK_TIME))
      .sort((first, second) => first.stage - second.stage || first.ordering - second.ordering);

    setDistance(distanceSum);

    await formik.setFieldValue('routeOrders', finalRouteOrders);

    if (travelToFirstPointMs.current) {
      // In 'first_pickup' mode the user's firstPickupAt is the source of truth
      // and departureAt must follow. In 'departure' mode it is the opposite.
      // Previously firstPickupAt was always recomputed from departureAt, which
      // silently overwrote user edits to firstPickupAt.
      if (formik.values.planningMode === 'first_pickup' && formik.values.firstPickupAt) {
        const pickup = toValidDate(formik.values.firstPickupAt);
        if (pickup) {
          const dep = new Date(pickup.getTime() - travelToFirstPointMs.current);
          formik.setFieldValue('departureAt', format(dep, 'yyyy-MM-dd HH:mm'));
          formik.setFieldValue('routeDate', format(dep, 'yyyy-MM-dd'));
          formik.setFieldValue('startTime', format(dep, 'HH:mm'));
        }
      } else if (formik.values.departureAt) {
        const dep = toValidDate(formik.values.departureAt);
        if (dep) {
          const pickup = new Date(dep.getTime() + travelToFirstPointMs.current);
          formik.setFieldValue('firstPickupAt', format(pickup, 'yyyy-MM-dd HH:mm'));
        }
      }
    }

    if (runId === timelineRunIdRef.current) {
      setIsTimelineCalculating(false);
    }
  };

  const invalidateRoute = () => {
    setIsRouteDirty(true);
    setHideForm(true);
  };

  const acceptRouteResult = (nextRouteResult) => {
    const nextDirections = nextRouteResult.data
      .filter(d => d?.status === 'success' && d?.directions?.routes?.length > 0)
      .map(({ directions, stageIndex, stageWaypoints }) => ({ directions, stageIndex, stageWaypoints }));

    setRouteResult(nextRouteResult);
    setIsRouteDirty(false);
    setIsLoading(false);
    setHideForm(false);
    setIsTimelineCalculating(nextDirections.length > 0);

    setDirections(nextDirections);
  };

  const handleUnloadingTimeChange = (id, { target: { value } }) => {
    const unloadingTime = parseInt(value) || 0;
    onUpdateRouteOrder(id, { unloadingTime });

    // Only the timeline needs to be recalculated; map data (distances, polyline)
    // is unaffected by unloading time, so we skip invalidateRoute().
    renderTimeLine();
  };

  const handleDailyBreakMinutesChange = (id, { target: { value } }) => {
    const dailyBreakMinutes = parseInt(value) || 0;
    handleUpdateRouteOrder(id, { dailyBreakMinutes });
  };

  const handleUnloadingTimeForAllChange = (unloadingTime) => {
    const updatedRouteOrders = routeOrders.map((ro) => ({
      ...ro,
      unloadingTime,
    }));
    onUpdateRouteOrders(updatedRouteOrders);

    // Map data (distances, polyline) is unaffected by unloading time —
    // only the timeline needs recalculating, same as the per-stop handler.
    renderTimeLine();
  };

  const handleRecalculateSpeed = () => {
    if (!formik.values.speed || formik.values.speed == 0) {
      return toastr.error(__('routes.no_speed_error'));
    }

    renderTimeLine();
  };

  const renderCover = () => {
    return (
      hideForm && (
        <div className="form-cover">
          <div className="form-cover__background" />
          <div className="form-cover__text">
            <i className="form-cover__icon bx bx-error" />
            {__('routes.form_cover')}
          </div>
        </div>
      )
    );
  };

  const handleUpdateRouteOrders = (updatedRouteOrders) => {
    onUpdateRouteOrders(updatedRouteOrders);

    invalidateRoute();
  };

  const handleUpdateOrder = (id, updatedOrder) => {
    onUpdateOrder(id, updatedOrder);

    setHasRouteOrdersChanged(true);
  };

  const handleUpdateRouteOrder = (id, data) => {
    onUpdateRouteOrder(id, data);

    invalidateRoute();
  };

  const handleEndPointChange = (event) => {
    formik.handleChange(event);
    formik.setFieldValue('endPointVia', []);
    formik.setFieldValue('endPointDistance', null);
    formik.setFieldValue('endPointArrivalDatetime', null);
    invalidateRoute();
  };

  const handleEndPointViaChange = (via) => {
    formik.setFieldValue('endPointVia', via);
  };

  const hasDepartureDate = !!(formik.values.departureAt || (formik.values.routeDate && formik.values.startTime));
  const disableButtons = !!formik.errors.speed;
  const canSaveRoute = hasDepartureDate
    && routeOrders.length > 0
    && Boolean(routeResult)
    && !isRouteDirty
    && !isTimelineCalculating;

  const handleAddProducts = (index, products) => {
    const newRouteOrders = [...formik.values.routeOrders];

    newRouteOrders.map((newRouteOrder, i) => {
      if (i === index) {
        newRouteOrder.products = products ? products.map((p) => {
          const productRouteOrder = p.routeOrders.find(({ routeOrderId }) => routeOrderId === newRouteOrder.id)

          return {
            ...p,
            disabledBy: !productRouteOrder && p.routeOrders?.length > 0 ? p.routeOrders[0] : null,
            disabled: !productRouteOrder && p.routeOrders?.length > 0,
            value: !!productRouteOrder
          }
        }) : []

        return newRouteOrder
      }

      return newRouteOrder
    })

    formik.setFieldValue('routeOrders', newRouteOrders);
  }

  const getRouteOrdersToCalculateArrivedTime = (routeOrders) => {
    return routeOrders
      .filter((routeOrder) => routeOrder.arrivalTime !== null)
      .map((routeOrder, index) => {
        return {
          routeOrderId: routeOrder.id ?? null,
          arrivalTime: routeOrder.arrivalTime,
          unloadingTime: routeOrder.unloadingTime,
          dailyBreakMinutes: routeOrder.dailyBreakMinutes ?? 0,
        }
      });
  }

  const calculateArrivedTime = async (routeOrders) => {
    try {
      const routeOrdersToCalculateArrivedTime = getRouteOrdersToCalculateArrivedTime(routeOrders);
      if (routeOrdersToCalculateArrivedTime.length) {
        const calculatedRouteOrders = await routesApi.calculate(routeOrdersToCalculateArrivedTime);
        return routeOrders.map((routeOrder, index) => {
          const calculated = calculatedRouteOrders[index];

          if (calculated) {
            const baseTime = routeOrder.arrivalDatetime ? routeOrder.arrivalDatetime.getTime() : 0;
            return {
              ...routeOrder,
              arrivalDatetime: routeOrder.arrivalDatetime ? new Date(baseTime + calculated.totalBufferInMs) : null,
              calculatedArrivalTime: calculated.newArrivalHours,
              routeBufferStop: {
                humanFormat: calculated.bufferForHumans,
                durationInMin: calculated.durationInMin,
              },
              isValid: calculated.isValid,
            };
          }

          return routeOrder;
        });
      }
    } catch (e) {
      console.error(e);
    }

    return routeOrders;
  }

  const calculateAndValidateArrivedTime = async () => {
    try {
      const routeOrdersToCalculateArrivedTime = getRouteOrdersToCalculateArrivedTime(formik.values.routeOrders);

      if (routeOrdersToCalculateArrivedTime.length) {
        const calculatedRouteOrders = await routesApi.calculateAndValidate(routeOrdersToCalculateArrivedTime);

        setValidatedRouteOrders(calculatedRouteOrders);

        return calculatedRouteOrders;
      }
    } catch (e) {
      console.error(e);
    }

    return null;
  }

  const onSubmitFromModal = async () => {
    if (submitLockRef.current) return;

    submitLockRef.current = true;

    try {
      await formik.submitForm();
    } finally {
      submitLockRef.current = false;
    }
  };

  const continueSubmitFlow = async () => {
    try {
      const calculatedRouteOrders = await calculateAndValidateArrivedTime();

      if (calculatedRouteOrders?.some(item => !item.isValid)) {
        submitLockRef.current = false;
        setIsOpenConfirmSaveModal(true);
        return;
      }

      await formik.submitForm();
    } catch (e) {
      console.error(e);
      submitLockRef.current = false;
    }
  }

  const getFirstRoutePointArrivalDate = () => {
    const firstRoutePoint = formik.values.routeOrders
      .filter((element) => element?.type === RouteElementType.ROUTE_ORDER)
      .find((element) => element?.arrivalDatetime);

    return toValidDate(firstRoutePoint?.arrivalDatetime);
  };

  const onSubmitWithFirstPointGapWarningAccepted = async () => {
    if (submitLockRef.current) return;

    submitLockRef.current = true;
    setIsOpenFirstPointGapWarningModal(false);
    await continueSubmitFlow();
  };

  const onSubmitForm = async () => {
    if (submitLockRef.current) return;

    submitLockRef.current = true;
    setShowSubmitValidationError(false);

    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      Object.keys(validationErrors).forEach((fieldName) => {
        formik.setFieldTouched(fieldName, true, false);
      });
      setShowSubmitValidationError(true);
      submitLockRef.current = false;
      return;
    }

    const loadingDate = toValidDate(formik.values.loadingAt);
    const departureDate = toValidDate(formik.values.departureAt);
    const firstRoutePointArrivalDate = getFirstRoutePointArrivalDate();

    if (loadingDate && departureDate && loadingDate > departureDate) {
      setIsOpenDeliveryWarningModal(true);
      submitLockRef.current = false;
      return;
    }

    if (loadingDate && firstRoutePointArrivalDate && differenceInCalendarDays(firstRoutePointArrivalDate, loadingDate) >= 2) {
      setIsOpenFirstPointGapWarningModal(true);
      submitLockRef.current = false;
      return;
    }

    await continueSubmitFlow();
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header tx-15 fw-bold">
              Filtry <button className='btn btn-primary' onClick={() => setFiltersVisible(prev => !prev)}>{filtersVisible ? 'Ukryj filtry' : 'Pokaż filtry'}</button>
            </div>
            <div className={cx('card-body', { 'hidden': !filtersVisible })}>
              <Formik>
                {(index) => (
                  <Form key={index}>
                    <div className='row'>
                      <p className='tx-1'>Klienci</p>
                    </div>
                    <div className='row'>
                      {senders.map(sender => (
                        <div className='col-4' key={sender.id}>
                          <fieldset key={sender.id}>
                            <div className="checkbox">
                              <input type="checkbox"
                                className="checkbox-input"
                                name="sender-filter"
                                value={sender.id ?? ''}
                                id={`checkbox-sender-${sender.id}`}
                                onChange={(event) => filterSendersHandler(event.target.value)}
                                checked={selectedSenders.indexOf(sender.id.toString()) !== -1}
                              />
                              <label htmlFor={`checkbox-sender-${sender.id}`}>
                                {sender.name}
                              </label>
                            </div>
                          </fieldset>
                        </div>
                      ))}
                    </div>
                    <div className='row'>
                      <p className='tx-1 mt-1'>Usługi</p>
                    </div>
                    <div className='row'>
                      {services.map((service, index) => (
                        <div key={service.id} className='col-4'>
                          <fieldset key={service.id}>
                            <div className="checkbox">
                              <input type="checkbox"
                                className="checkbox-input service"
                                name="service-filter"
                                value={service.id ?? ''}
                                id={`checkbox-service-${service.id}`}
                                onChange={(event) => filterServiceHandler(event.target.value)}
                                checked={selectedServices.indexOf(service.id.toString()) !== -1}
                              />
                              <label htmlFor={`checkbox-service-${service.id}`}>
                                {service.name}
                              </label>
                            </div>
                          </fieldset>
                        </div>
                      ))}
                    </div>
                    <div className='row'>
                      <p className='tx-1 mt-1'>Status</p>
                    </div>
                    <div className='row'>
                      {statuses.map((status, index) => (
                        <div key={status.id} className='col-4'>
                          <fieldset key={status.id}>
                            <div className="checkbox">
                              <input type="checkbox"
                                className="checkbox-input status"
                                name="status-filter"
                                value={status.id ?? ''}
                                id={`checkbox-status-${status.id}`}
                                onChange={(event) => filterStatusesHandler(event.target.value)}
                                checked={selectedStatuses.indexOf(status.id.toString()) !== -1}
                              />
                              <label htmlFor={`checkbox-status-${status.id}`}>
                                {status.name}
                              </label>
                            </div>
                          </fieldset>
                        </div>
                      ))}
                    </div>

                    <div className='row'>
                      <p className='tx-1 mt-1'>{__('orders.orders')}</p>
                    </div>

                    <div className='row'>
                      <div key='is_urgent' className='col-4'>
                        <fieldset key='is_urgent'>
                          <div className="checkbox">
                            <input type="checkbox"
                              className="checkbox-input is_urgent"
                              name="is_urgent"
                              value="1"
                              id={`checkbox-is-urgent`}
                              onChange={(event) => filterIsUrgentHandler(event.target.value)}
                              checked={selectedIsUrgentFilter[0] === "1"}
                            />

                            <label htmlFor={`checkbox-is-urgent`}>
                              {__('orders.show_urgent_orders')}
                            </label>
                          </div>
                        </fieldset>
                      </div>
                    </div>

                    <div className='row mt-1'>
                      <div key='is_order' className='col-4'>
                        <fieldset key='is_order'>
                          <div className="checkbox">
                            <input type="checkbox"
                                   className="checkbox-input is_order"
                                   name="is_order"
                                   value="1"
                                   id={`checkbox-is-order`}
                                   onChange={(event) => {
                                     filterIsOrderHandler(event.target.value)
                                     filterIsReturnHandler('')
                                     filterIsExchangeHandler('')
                                   }}
                                   checked={selectedIsOrderFilter[0] === "1"}
                            />

                            <label htmlFor={`checkbox-is-order`}>
                              {__('orders.show_only_orders')}
                            </label>
                          </div>
                        </fieldset>
                      </div>

                      <div key='is_exchange' className='col-4'>
                        <fieldset key='is_exchange'>
                          <div className="checkbox">
                            <input type="checkbox"
                                   className="checkbox-input is_exchange"
                                   name="is_exchange"
                                   value="1"
                                   id={`checkbox-is-exchange`}
                                   onChange={(event) => {
                                     filterIsOrderHandler('')
                                     filterIsReturnHandler('')
                                     filterIsExchangeHandler(event.target.value)
                                   }}
                                   checked={selectedIsExchangeFilter[0] === "1"}
                            />

                            <label htmlFor={`checkbox-is-exchange`}>
                              {__('orders.show_only_exchange_orders')}
                            </label>
                          </div>
                        </fieldset>
                      </div>

                      <div key='is_return' className='col-4'>
                        <fieldset key='is_return'>
                          <div className="checkbox">
                            <input type="checkbox"
                                   className="checkbox-input is_return"
                                   name="is_return"
                                   value="1"
                                   id={`checkbox-is-return`}
                                   onChange={(event) => {
                                     filterIsOrderHandler('')
                                     filterIsReturnHandler(event.target.value)
                                     filterIsExchangeHandler('')
                                   }}
                                   checked={selectedIsReturnFilter[0] === "1"}
                            />

                            <label htmlFor={`checkbox-is-return`}>
                              {__('orders.show_only_return_orders')}
                            </label>
                          </div>
                        </fieldset>
                      </div>
                    </div>

                    <div className='row mt-1'>
                      <div key={index} className='col-4'>
                        <fieldset key="location_filter">
                          <label htmlFor="location_filter">{__('routes.location_filter')}:</label>
                          <select
                            className="select2 form-control select2-hidden-accessible"
                            id="location_filter"
                            name="location_filter"
                            value={selectedLocationFilter[0] ?? ''}
                            onChange={(event) => {
                              filterLocationFilterHandler("");
                              filterLocationFilterHandler(event.target.value);
                            }}
                          >
                            <option value="">{__('routes.location_filter_options.all')}</option>
                            <option value="warehouse">{__('routes.location_filter_options.warehouse')}</option>
                            <option value="visible_by_pickup_at">{__('routes.location_filter_options.visible_by_pickup_at')}</option>
                          </select>
                        </fieldset>
                      </div>
                    </div>

                    <div className="row mt-1">
                      <div className="col-12">
                        <MapFilters
                          confirmationsFilter={confirmationsFilter}
                          onConfirmationsChange={setConfirmationsFilter}
                          warehouseDaysFilter={warehouseDaysFilter}
                          onWarehouseDaysChange={setWarehouseDaysFilter}
                        />
                      </div>
                    </div>

                    {productTypes.length > 1 && (
                      <>
                        <div className='row'>
                          <p className='tx-1 mt-1'>Cechy produktu</p>
                        </div>
                        <div className='row'>
                          {productTypes.map((productType) => (
                            <div className='col-4' key={productType.id}>
                              <fieldset key={productType.id}>
                                <div className="checkbox">
                                  <input type="checkbox"
                                    className="checkbox-input product-type"
                                    name="product-type-filter"
                                    value={productType.id ?? ''}
                                    id={`checkbox-product-type-${productType.id}`}
                                    onChange={(event) => filterProductTypesHandler(event.target.value)}
                                    checked={selectedProductTypes.indexOf(productType.id.toString()) !== -1}
                                  />
                                  <label htmlFor={`checkbox-product-type-${productType.id}`}>
                                    {productType.id === -1 ? 'Bez cechy' : productType.name}
                                  </label>
                                </div>
                              </fieldset>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    <div className="row">
                    </div>
                    <div className="mt-1">
                      <button
                        className='btn btn-warning mr-5px'
                        type="button"
                        onClick={() => {
                          filterSendersHandler("");
                          filterServiceHandler("");
                          filterStatusesHandler("");
                          filterLocationFilterHandler("");
                          setConfirmationsFilter(null);
                          setWarehouseDaysFilter(null);
                          filterProductTypesHandler("");
                        }}
                      >
                        Wyczyść
                      </button>
                      <button
                        className='btn btn-success'
                        type="button"
                        onClick={() => {
                          filterSendersHandler("");
                          filterServiceHandler("");
                          filterStatusesHandler("");
                          filterLocationFilterHandler("");
                          setConfirmationsFilter(null);
                          setWarehouseDaysFilter(null);
                          filterProductTypesHandler("");
                          senders?.forEach(sender => filterSendersHandler(sender.id?.toString()));
                          services?.forEach(service => filterServiceHandler(service.id?.toString()));
                          statuses?.forEach(status => filterStatusesHandler(status.id?.toString()));
                          if (productTypes.length > 1) {
                            productTypes?.forEach(pt => filterProductTypesHandler(pt.id?.toString()));
                          }
                        }}
                      >
                        Zaznacz wszystkie
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      <RouteMapContainer
        disableButtons={disableButtons}
        orders={filteredOrders}
        routeOrders={routeOrders}
        formRouteOrders={formik.values.routeOrders}
        onEndPointViaChange={handleEndPointViaChange}
        onUpdateOrder={handleUpdateOrder}
        onUpdateRouteOrders={handleUpdateRouteOrders}
        onUpdateRouteOrder={handleUpdateRouteOrder}
        confirmedVolumeM3={confirmedVolumeM3}
        totalVolumeM3={totalVolumeM3}
        confirmedIncomePrice={confirmedIncomePrice}
        totalIncomePrice={totalIncomePrice}
        isNotPricedPackages={isNotPricedPackages}
        orderConfirmedIncomePrice={orderConfirmedIncomePrice}
        orderTotalIncomePrice={orderTotalIncomePrice}
        exchangeConfirmedIncomePrice={exchangeConfirmedIncomePrice}
        exchangeTotalIncomePrice={exchangeTotalIncomePrice}
        distance={distance}
        routeCar={route.car}
        routeId={route.id}
        startPoint={{
          address: formik.values.startPoint,
        }}
        endPoint={{
          address: formik.values.endPoint,
          via: formik.values.endPointVia,
        }}
        departureTime={departureTime}
        onRouteCalculated={acceptRouteResult}
        handleBeforeApiCall={setDriverBreaks}
        hasReturnPicking={hasReturnPicking}
      />
      <form>
        <div className="row">
          <div className="col-12">
            <Card title={__('routes.options') + ' ' + (route.routeNumber ?? '')}>
              <RouteForm
                formik={formik}
                route={route}
                cars={cars}
                ramps={ramps}
                phones={phones}
                drivers={drivers}
                planningMode={formik.values.planningMode}
                onPlanningModeChange={(mode) => formik.setFieldValue('planningMode', mode)}
                onEndPointChange={handleEndPointChange}
                handleRecalculateSpeed={handleRecalculateSpeed}
                handleUnloadingTimeForAllChange={handleUnloadingTimeForAllChange}
                travelToFirstPointMs={effectiveTravelToFirstPointMs}
              />
            </Card>
          </div>
        </div>

        {routeOrders.length > 0 && (
          <div className="row">
            <div className="col-12">
              <Card title={__('volumes.volumes')}>
                <VolumeInfo
                  confirmedVolumeM3={confirmedVolumeM3}
                  totalVolumeM3={totalVolumeM3}
                  routeCar={route.car}
                />
              </Card>
            </div>
          </div>
        )}

        {routeOrders.length > 0 && (
          <div className="row">
            <div className="col-12">
              <Card title={__('routes.route')} content={renderCover()}>
                {hasDepartureDate ? (
                  <TimeLine
                    editable={true}
                    routeOrders={formik.values.routeOrders}
                    hasReturnPicking={hasReturnPicking}
                    onAddProducts={handleAddProducts}
                    onProductChange={formik.handleChange}
                    startTime={formik.values.startTime}
                    onUnloadingTimeChange={handleUnloadingTimeChange}
                    onDailyBreakMinutesChange={handleDailyBreakMinutesChange}
                    endPoint={{
                      address: formik.values.endPoint,
                      arrivalDatetime: formik.values.endPointArrivalDatetime,
                      distance: formik.values.endPointDistance,
                    }}
                    distanceSum={distance}
                    validatedRouteOrders={validatedRouteOrders}
                    orderMissingServicesPrices={orderMissingServicesPrices}
                    setRouteOrders={onUpdateRouteOrders}
                    routeId={route.id}
                  />
                ) : (
                  <div className="alert alert-info mb-2">
                    {__('routes.set_departure_to_see_timeline')}
                  </div>
                )}
                <div className="form-group">
                  <Button className="btn-primary" onClick={onSubmitForm} disabled={!canSaveRoute}>
                    Zapisz
                  </Button>
                  {showSubmitValidationError && Object.keys(formik.errors).length > 0 && (
                    <div className="invalid-feedback d-block">{__('routes.invalid_form')}</div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}
      </form>
      <ConfirmSaveModal
        isOpenConfirmSaveModal={isOpenConfirmSaveModal}
        setIsOpenConfirmSaveModal={setIsOpenConfirmSaveModal}
        sendRouteEditForm={onSubmitFromModal}
      />

      <Modal isOpen={isOpenDeliveryWarningModal} toggle={() => setIsOpenDeliveryWarningModal(false)}>
        <ModalHeader toggle={() => setIsOpenDeliveryWarningModal(false)}>
          {__('routes.validation.error_title')}
        </ModalHeader>
        <ModalBody>
          {__('routes.validation.delivery_date_warning')}
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-primary" onClick={() => setIsOpenDeliveryWarningModal(false)}>
            OK
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isOpenFirstPointGapWarningModal} toggle={() => setIsOpenFirstPointGapWarningModal(false)}>
        <ModalHeader toggle={() => setIsOpenFirstPointGapWarningModal(false)}>
          {__('routes.validation.warning_title')}
        </ModalHeader>
        <ModalBody>
          {__('routes.validation.first_point_gap_warning')}
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-secondary" onClick={() => setIsOpenFirstPointGapWarningModal(false)}>
            {__('routes.validation.cancel_editing')}
          </Button>
          <Button className="btn btn-primary" onClick={onSubmitWithFirstPointGapWarningAccepted}>
            {__('routes.validation.continue_anyway')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
